import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import MapModal from '../MapModal';
import 'leaflet/dist/leaflet.css';

const Container= styled.div`
   padding: 1rem;
   background-color: #f0f4f8;
   text-align:left; 
   border-radius:5px;

     p, a {
    word-wrap: break-word;
    overflow-wrap: break-word;
    word-break: break-word; /* Optional fallback */
  }

  @media (max-width: 768px) {
   text-align:center;
   p{
     display:flex;
     flex-direction:column;
     font-size: 14px;
   }
  }
`;



const SinglePhotoContainer = styled.div`
  display: flex;
  flex-direction:column;
  align-items: flex-start;
  margin-top: 10px;
  margin-bottom: 20px;

  img{
    border-radius: 5px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;


const PhotoItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  align-items: flex-start;
  margin-top: 10px;
  margin-bottom: 20px;
  img{
    border-radius: 5px;
  }

    @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

// Modal to display the full image
const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  visibility: ${(props) => (props.visible ? 'visible' : 'hidden')};
`;

const ModalImage = styled.img`
  max-width: 90%;
  max-height: 90%;
  object-fit: contain;
`;

// FilePlaceTypeEnum
const FILE_PLACE_TYPE = {
  Gallery: 0,
  CategoryPhoto: 1,
  CoverPhoto: 2,
  Poster: 3,
};

const ExperienceDetails = ({ experience}) => {
  const { i18n } = useTranslation();
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const tokenString = sessionStorage.getItem("token");
  const [showModal, setShowModal] = useState(false);
  const [modalImage, setModalImage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showMapModal, setShowMapModal] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);
  
     // Initialize form with default structure to avoid undefined errors
      const [form, setForm] = useState({
        name: { gr: '', en: '' },
        description: { gr: '', en: '' },
        location: { latitude: '', longitude: '' },
        gpxLink: '',
        categoryId: '',
        videoLink:'',
        coverPhoto: null,
        categoryPhoto: null,
        photos: [],
        existingCoverFileName: null,
        existingCategoryFileName: null,
        // Add existing photos filenames if needed to handle delete on server
        existingGalleryFileNames: [], // optional
      });
  
     useEffect(() => {
        const fetchData = async () => {
          try {
            const response = await fetch(`${BASE_URL}Experience/${experience.id}`, {
              headers: {
                Authorization: `Bearer ${tokenString}`,
              },
            });
            if (!response.ok) throw new Error("Failed to fetch experience data");
            const data = await response.json();
            const rawFileNames = data.fileNames; 
            let fetchedFiles = [];
    
          if (rawFileNames.length > 0) {
            const fileResponse = await fetch(`${BASE_URL}Experience/getFiles`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${tokenString}`,
              },
              body: JSON.stringify( rawFileNames),
            });
    
            if (!fileResponse.ok) throw new Error("Failed to fetch file contents");
            fetchedFiles = await fileResponse.json();
          }
    
          // Process fetched files based on their filePlaceType
          const {
            existingCoverFileName,
            existingCategoryFileName,
            existingGalleryFileNames, 
            galleryBase64Srcs,
            coverBase64Src,
            categoryBase64Src,
          } = processFiles(fetchedFiles);
    
          setForm({
            name: data.name || { gr: '', en: '' },
            description: data.description || { gr: '', en: '' },
            location: data.location || { latitude: '', longitude: '' },
            gpxLink: data.gpxLink|| '',
            categoryId: data.categoryId || '',
            videoLink: data.videoLink || '',
            coverPhoto: null,
            categoryPhoto: null,
            photos: [],
            existingCoverFileName,
            existingCategoryFileName,
             existingGalleryFileNames,
            galleryBase64Srcs,
            coverBase64Src,
            categoryBase64Src,
          });
    
          } catch (err) {
            console.error('Error loading experience:', err);
          }
           finally {
            setIsLoading(false); // End loading
          }
        };
        fetchData();
      }, [BASE_URL, experience.id, tokenString]);
  
    const processFiles = (files) => {
    const coverFile = files.find(f => f.filePlaceType === FILE_PLACE_TYPE.CoverPhoto);
    const categoryFile = files.find(f => f.filePlaceType === FILE_PLACE_TYPE.CategoryPhoto);
    const galleryFiles = files.filter(f => f.filePlaceType === FILE_PLACE_TYPE.Gallery);
  
    const coverBase64Src = coverFile ? `data:${coverFile.mimeType};base64,${coverFile.base64Content}` : null;
    const categoryBase64Src = categoryFile ? `data:${categoryFile.mimeType};base64,${categoryFile.base64Content}` : null;
    const galleryBase64Srcs = galleryFiles.map(f => `data:${f.mimeType};base64,${f.base64Content}`);
  
    // Collect filenames of gallery files to handle deletions later
    const existingGalleryFileNames = galleryFiles.map(f => f.fileName);
  
  
    return {
      existingCoverFileName: coverFile?.fileName || null,
      existingCategoryFileName: categoryFile?.fileName || null,
       existingGalleryFileNames,  
      galleryBase64Srcs,
      coverBase64Src,
      categoryBase64Src,
    };
  };
  
   const handleOpenModal = (img) => {
      if (!img) return;
      setModalImage(typeof img === 'string' ? img : URL.createObjectURL(img));
      setShowModal(true);
    };
  
    const handleCloseModal = () => {
      setShowModal(false);
      setModalImage('');
    };

  return (
    <Container>

      <p><strong>{i18n.t('name')} (English):</strong> {experience.name.en}</p>
      <p><strong>{i18n.t('name')} (Ελληνικά):</strong> {experience.name.gr}</p>
      <p><strong>{i18n.t('description')} (English):</strong> <div style={{ whiteSpace: "pre-line" }}>{experience.description.en}</div></p>
      <p><strong>{i18n.t('description')} (Ελληνικά):</strong> <div style={{ whiteSpace: "pre-line" }}>{experience.description.gr}</div></p>
      <p><strong>{i18n.t('category')} (English):</strong> {experience.categoryName.en}</p>
      <p><strong>{i18n.t('category')} (Ελληνικά):</strong> {experience.categoryName.gr}</p>
       <p style={{ cursor: 'pointer', textDecoration:'underline' }} 
        onClick={() => {
          setSelectedPosition([experience.location.latitude, experience.location.longitude]);
          setShowMapModal(true);
        }}><strong>{i18n.t('location')}:</strong>
         {experience.location.longitude}, {experience.location.latitude}
      </p>
      <p><strong>{i18n.t('videoLink')}:</strong> <a href={experience.videoLink} target="_blank" rel="noopener noreferrer">{experience.videoLink}</a></p>
      <p><strong>{i18n.t('experiencePath')}:</strong> {experience.gpxLink}</p>
      
       <>
        <p><strong>{i18n.t('coverPhoto')}:</strong></p>
        {isLoading ? (
          <p>{i18n.t('loading')}</p>
        ) : form.coverPhoto || form.coverBase64Src ? (
          <SinglePhotoContainer>
            <img
              src={
                form.coverPhoto
                  ? URL.createObjectURL(form.coverPhoto)
                  : form.coverBase64Src
              }
              alt="Cover"
              style={{ width: 120, height: 120, objectFit: 'cover', cursor: 'pointer' }}
              onClick={() =>
                handleOpenModal(
                  form.coverPhoto
                    ? URL.createObjectURL(form.coverPhoto)
                    : form.coverBase64Src
                )
              }
            />
          </SinglePhotoContainer>
        ) : (
          <p>{i18n.t('noFiles')}</p> 
        )}
      </>

      <>
        <p><strong>{i18n.t('categoryPhoto')}:</strong></p>
        {isLoading ? (
          <p>{i18n.t('loading')}</p>
        ) : form.categoryPhoto || form.existingCategoryFileName ? (
          <SinglePhotoContainer>
            <img
              src={
                form.categoryPhoto
                  ? URL.createObjectURL(form.categoryPhoto)
                  : form.categoryBase64Src
              }
              alt="Category"
              style={{ width: 120, height: 120, objectFit: 'cover', cursor: 'pointer' }}
              onClick={() =>
                handleOpenModal(
                  form.categoryPhoto
                    ? URL.createObjectURL(form.categoryPhoto)
                    : form.categoryBase64Src
                )
              }
            />
          </SinglePhotoContainer>
        ) : (
          <p>{i18n.t('noFiles')}</p>
        )}
      </>
      
      <>
        <p><strong>{i18n.t('photos')}:</strong></p>
        {isLoading ? (
          <p>{i18n.t('loading')}</p>
        ) : form.galleryBase64Srcs && form.galleryBase64Srcs.length > 0 ? (
          form.galleryBase64Srcs.map((src, index) => (
            <PhotoItem key={`existing-${index}`}>
              <img
                src={src}
                alt={`Gallery existing ${index}`}
                style={{ width: 100, height: 100, objectFit: 'cover', cursor: 'pointer' }}
                onClick={() => handleOpenModal(src)}
              />
            </PhotoItem>
          ))
        ) : (
          <p>{i18n.t('noFiles')}</p>
        )}
      </>

      <Modal visible={showModal} onClick={handleCloseModal}>
        <ModalImage src={modalImage} alt="Full screen view" onClick={(e) => e.stopPropagation()} />
      </Modal>

       {showMapModal && selectedPosition && (
          <MapModal
            position={selectedPosition}
            onClose={() => setShowMapModal(false)}
          />
        )}

    </Container>
  );
};

export default ExperienceDetails;
